import type { Currency, SalaryDisplayFormat, SalaryPeriod, SalaryRange } from '../types'

// Exchange rates relative to USD (base currency)
// These are approximate rates - in production, you'd fetch from an API
// Last updated: Dec 2024
const EXCHANGE_RATES: Record<Exclude<Currency, 'original'>, number> = {
  USD: 1,
  EUR: 0.92,    // 1 USD = 0.92 EUR
  GBP: 0.79,    // 1 USD = 0.79 GBP
  PLN: 4.0,     // 1 USD = 4.0 PLN
  CHF: 0.88,    // 1 USD = 0.88 CHF
  CZK: 23.5,    // 1 USD = 23.5 CZK
  SEK: 10.8,    // 1 USD = 10.8 SEK
  NOK: 11.0,    // 1 USD = 11.0 NOK
  DKK: 6.9,     // 1 USD = 6.9 DKK
  CAD: 1.36,    // 1 USD = 1.36 CAD
  AUD: 1.55,    // 1 USD = 1.55 AUD
}

// Hours per month (standard working hours)
export const HOURS_PER_MONTH = 168
export const MONTHS_PER_YEAR = 12

// Currency symbols
export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  PLN: 'zł',
  CHF: 'Fr',
  CZK: 'Kč',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  CAD: 'C$',
  AUD: 'A$',
  original: '',  // No symbol for original
}

// Currency labels for UI
export const CURRENCY_LABELS: Record<Currency, string> = {
  USD: 'US Dollar ($)',
  EUR: 'Euro (€)',
  GBP: 'British Pound (£)',
  PLN: 'Polish Zloty (zł)',
  CHF: 'Swiss Franc (Fr)',
  CZK: 'Czech Koruna (Kč)',
  SEK: 'Swedish Krona (kr)',
  NOK: 'Norwegian Krone (kr)',
  DKK: 'Danish Krone (kr)',
  CAD: 'Canadian Dollar (C$)',
  AUD: 'Australian Dollar (A$)',
  original: 'as posted',
}

// All supported currencies in display order
export const SUPPORTED_CURRENCIES: Currency[] = [
  'USD', 'EUR', 'GBP', 'PLN', 'CHF', 'CZK', 'SEK', 'NOK', 'DKK', 'CAD', 'AUD'
]

/**
 * Convert amount from one currency to another
 */
export const convertCurrency = (
  amount: number | null | undefined,
  fromCurrency: Currency | string,
  toCurrency: Exclude<Currency, 'original'>
): number | null => {
  if (amount == null || !Number.isFinite(amount)) return null
  
  const from = (fromCurrency === 'original' || !((fromCurrency as Currency) in EXCHANGE_RATES)) 
    ? 'USD' 
    : (fromCurrency as Exclude<Currency, 'original'>)
  
  // Convert to USD first (base), then to target
  const amountInUsd = amount / EXCHANGE_RATES[from]
  const converted = amountInUsd * EXCHANGE_RATES[toCurrency]
  
  return Math.round(converted)
}

/**
 * Convert salary range to target currency
 */
export const convertSalaryRange = (
  range: SalaryRange,
  toCurrency: Currency
): SalaryRange => {
  if (!range) return null

  // If target is 'original', return as-is
  if (toCurrency === 'original') return range

  const fromCurrency = (range.currency as Currency) || 'USD'

  // If already in target currency, return as-is
  if (fromCurrency === toCurrency) return range

  return {
    min: convertCurrency(range.min, fromCurrency, toCurrency) ?? 0,
    max: convertCurrency(range.max, fromCurrency, toCurrency) ?? 0,
    currency: toCurrency,
    period: range.period,
  }
}

/**
 * Format currency amount with symbol
 */
export const formatCurrencyAmount = (
  amount: number | null | undefined,
  currency: Currency,
  options?: { compact?: boolean }
): string => {
  if (amount == null || !Number.isFinite(amount)) return '—'
  
  const formatted = options?.compact
    ? amount >= 1000
      ? `${Math.round(amount / 1000)}k`
      : amount.toString()
    : amount.toLocaleString('en-US')
  
  const symbol = CURRENCY_SYMBOLS[currency] || currency
  
  // Symbol placement - prefix for most, suffix for some
  switch (currency) {
    case 'USD':
    case 'EUR':
    case 'GBP':
    case 'CAD':
    case 'AUD':
      return `${symbol}${formatted}`
    case 'PLN':
    case 'CZK':
    case 'SEK':
    case 'NOK':
    case 'DKK':
    case 'CHF':
    default:
      return `${formatted} ${symbol}`
  }
}

/**
 * Format salary range with currency conversion
 */
export const formatSalaryRangeConverted = (
  range: SalaryRange,
  targetCurrency: Currency,
  conversionEnabled: boolean
): string => {
  if (!range) return '—'
  
  const displayRange = conversionEnabled 
    ? convertSalaryRange(range, targetCurrency) 
    : range
  
  if (!displayRange) return '—'
  
  const currency = displayRange.currency as Currency || 'USD'
  const min = formatCurrencyAmount(displayRange.min, currency)
  const max = formatCurrencyAmount(displayRange.max, currency)
  
  return `${min} – ${max}`
}

/**
 * Get exchange rate info text
 */
export const getExchangeRateInfo = (targetCurrency: Currency): string => {
  if (targetCurrency === 'original' || targetCurrency === 'USD') return ''
  
  const rate = EXCHANGE_RATES[targetCurrency as Exclude<Currency, 'original'>]
  return `1 USD ≈ ${rate.toFixed(2)} ${targetCurrency}`
}

/**
 * Convert monthly amount to target format
 */
export const convertToDisplayFormat = (
  monthlyAmount: number | null | undefined,
  format: SalaryDisplayFormat
): number | null => {
  if (monthlyAmount == null || !Number.isFinite(monthlyAmount)) return null
  
  switch (format) {
    case 'hourly':
      return Math.round(monthlyAmount / HOURS_PER_MONTH)
    case 'yearly':
      return Math.round(monthlyAmount * MONTHS_PER_YEAR)
    case 'monthly':
    case 'original':
    default:
      return monthlyAmount
  }
}

/**
 * Get period suffix for display format
 */
export const getFormatSuffix = (format: SalaryDisplayFormat): string => {
  switch (format) {
    case 'hourly':
      return '/h'
    case 'yearly':
      return '/yr'
    case 'monthly':
      return '/mo'
    case 'original':
    default:
      return ''
  }
}

/**
 * Format labels for salary display format dropdown
 */
export const SALARY_FORMAT_LABELS: Record<SalaryDisplayFormat, string> = {
  original: 'as posted',
  hourly: 'Hourly',
  monthly: 'Monthly',
  yearly: 'Yearly',
}

/**
 * Format salary range with display format conversion
 */
export const formatSalaryWithFormat = (
  range: SalaryRange,
  _currency: Currency,
  format: SalaryDisplayFormat,
  currencyConversionEnabled: boolean,
  targetCurrency: Currency
): string => {
  if (!range) return '—'
  
  // Apply currency conversion first
  const convertedRange = currencyConversionEnabled
    ? convertSalaryRange(range, targetCurrency)
    : range
  
  if (!convertedRange) return '—'
  
  const displayCurrency = convertedRange.currency as Currency || 'USD'
  
  // Apply format conversion
  const min = convertToDisplayFormat(convertedRange.min, format)
  const max = convertToDisplayFormat(convertedRange.max, format)
  
  if (min == null || max == null) return '—'
  
  const suffix = getFormatSuffix(format)
  const minFormatted = formatCurrencyAmount(min, displayCurrency)
  const maxFormatted = formatCurrencyAmount(max, displayCurrency)

  return `${minFormatted} – ${maxFormatted}${suffix}`
}

/**
 * Convert amount between salary periods
 */
export const convertPeriod = (
  amount: number | null | undefined,
  fromPeriod: SalaryPeriod,
  toPeriod: SalaryPeriod
): number | null => {
  if (amount == null || !Number.isFinite(amount)) return null
  if (fromPeriod === toPeriod) return amount
  if (!fromPeriod || !toPeriod) return amount

  // Convert to monthly (base unit)
  let monthly: number
  switch (fromPeriod) {
    case 'hourly':
      monthly = amount * HOURS_PER_MONTH
      break
    case 'yearly':
      monthly = amount / MONTHS_PER_YEAR
      break
    case 'monthly':
    default:
      monthly = amount
  }

  // Convert from monthly to target
  switch (toPeriod) {
    case 'hourly':
      return Math.round(monthly / HOURS_PER_MONTH)
    case 'yearly':
      return Math.round(monthly * MONTHS_PER_YEAR)
    case 'monthly':
    default:
      return Math.round(monthly)
  }
}

/**
 * Get period suffix for SalaryPeriod
 */
export const getPeriodSuffix = (period: SalaryPeriod): string => {
  switch (period) {
    case 'hourly':
      return '/h'
    case 'yearly':
      return '/yr'
    case 'monthly':
      return '/mo'
    default:
      return ''
  }
}

/**
 * Format salary with period label
 */
export const formatSalaryWithPeriod = (
  amount: number | null | undefined,
  currency: Currency,
  period: SalaryPeriod
): string => {
  if (amount == null || !Number.isFinite(amount)) return '—'
  const formatted = formatCurrencyAmount(amount, currency)
  return `${formatted}${getPeriodSuffix(period)}`
}

/**
 * Convert salary range to different period
 */
export const convertSalaryRangePeriod = (
  range: SalaryRange,
  targetPeriod: SalaryPeriod
): SalaryRange => {
  if (!range || !targetPeriod) return range
  if (range.period === targetPeriod) return range

  return {
    min: convertPeriod(range.min, range.period, targetPeriod) ?? 0,
    max: convertPeriod(range.max, range.period, targetPeriod) ?? 0,
    currency: range.currency,
    period: targetPeriod,
  }
}
